import * as React from 'react';
import { FluentDialog } from './FluentDialog';
import { Link, Text, Image, makeStyles, Display, Title1 } from '@fluentui/react-components';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { copyToClipboard } from '../util/Clipboard';
import { CenteredContent } from './CenteredContent';
import { flexColumn } from '../styles/GlobalStyles';

const useStyles = makeStyles({
  flexContainer: {
    ...flexColumn,
    justifyContent: "center"
  },
  centeredText: {
    alignSelf: "center",
    textAlign: "center",
    width: "80%"
  },
  image: {
    alignSelf: "center",
    width: "30%",
    height: "auto"
  }
});

export const ErrorBoundaryUI: React.FC = () =>
{
    const styles = useStyles();
    const error = useRouteError() as Error;
    const navigate = useNavigate();

    const copyError = async () => {
        await copyToClipboard(`${error?.message}\n\n${error?.stack}`);
    };

    const resetError = () => {
        // Navigate to a safe route (like home) to reset the error
        navigate("/", { replace: true });
    };

    // If it's a "not found" response, let React Router handle it (or render a simple fallback)
    if (isRouteErrorResponse(error) && error.status === 404) {
        return (
            <CenteredContent>
                <div className={styles.flexContainer}>
                    <Display className={styles.centeredText}>404</Display>
                    <br />
                    <Image src='/logo.png' className={styles.image} />
                    <br />
                    <Title1 className={styles.centeredText}>
                        Two faces, countless doorways… yet this one leads nowhere.
                    </Title1>
                </div>
            </CenteredContent>
        )
    }

    return (
        <FluentDialog
            open={true}
            title='An error occurred'
            submitLabel='Copy'
            onSubmit={copyError}
            cancelLabel='Back to start page'
            onCancel={resetError}
        >
            <p>
                <Text>We encountered an error while rendering the site:</Text>
                <br />
                <Text>{error.message}</Text>
                <br />
                <Text style={{ whiteSpace: "pre-wrap" }}>{error.stack}</Text>
                <br />
                <br />
                <Text>
                    Please use the copy button below to copy the error and open an issue{" "}
                    <Link href="https://github.com/Ianua-Software/Ianus-Clients/issues" target="_blank">
                        in our GitHub repo
                    </Link>.
                </Text>
            </p>
        </FluentDialog>
    );
};
